import React, { useMemo, useState } from 'react'
import {
  Modal,
  Input,
  Table,
  Typography,
  Select,
  InputNumber,
  Tooltip,
  Collapse,
  Tag,
} from 'antd'
import { InfoCircleOutlined } from '@ant-design/icons'
import styled from 'styled-components/macro'
import { calculateIngredientUsage } from 'src/utils/recipeIngredientCalculator'
import { getUsagePreviewConfig } from 'src/utils/addSoldRecipesUsagePreview'
import { WASTAGE_REASON_CHIPS } from 'src/constants/wastage'

const { Paragraph, Title } = Typography
const { Search } = Input
const { Option } = Select
const { Panel } = Collapse

const getRecipeIdString = recipeOrId => {
  if (!recipeOrId) return undefined
  if (typeof recipeOrId === 'string') return recipeOrId
  return recipeOrId._id || recipeOrId.id || recipeOrId?.toString?.()
}

export const buildRecipeEntriesFromState = (entryByRecipeId, recipes = []) =>
  Object.values(entryByRecipeId || {})
    .filter(item => {
      const qty = Number(item.qtySold) || 0
      return qty > 0 && item.recipeId
    })
    .map(item => {
      const recipe =
        (recipes || []).find(r => getRecipeIdString(r) === item.recipeId) || {}

      const recipePortions = Number(recipe.portions) || 1
      const qtySold = Number(item.qtySold) || 0
      const unitType = item.unitType || 'Portions'
      const totalPortionsSold =
        unitType === 'Full Recipe' ? qtySold * recipePortions : qtySold

      return {
        recipeId: item.recipeId,
        recipeName: item.recipeName || recipe.title || '',
        unitType,
        qtySold,
        totalPortionsSold,
      }
    })

const AddSoldRecipesModal = ({
  visible,
  onCancel,
  onSave,
  recipes = [],
  existingSoldRecipes = [],
  products = [],
  isPartialStockTake = false,
  partialScopeProductKeys = [],
  modalTitle = 'Sold Recipes',
  okText = 'Save Sold Recipes',
  quantityLabel = 'Qty Sold',
  usageField = 'sold',
  previewTitle,
  previewEmptyText,
  previewErrorText,
  usageQtyColumnLabel,
  helperDescription = `Add how many portions or full recipes you sold during this stock period. OMS will automatically calculate ingredient usage and update your expected stock.`,
  previewNote = '',
  showReason = false,
  reason = '',
  onReason,
  confirmLoading = false,
  recordedOn = '',
}) => {
  const usagePreviewConfig = getUsagePreviewConfig(usageField)
  const resolvedPreviewTitle = previewTitle || usagePreviewConfig.previewTitle
  const resolvedPreviewEmptyText =
    previewEmptyText || usagePreviewConfig.previewEmptyText
  const resolvedPreviewErrorText =
    previewErrorText || usagePreviewConfig.previewErrorText
  const resolvedUsageQtyColumnLabel =
    usageQtyColumnLabel || usagePreviewConfig.usageQtyColumnLabel
  const [searchTerm, setSearchTerm] = useState('')
  const [soldByRecipeId, setSoldByRecipeId] = useState({})
  const [reasonError, setReasonError] = useState('')

  // Map existing sold recipes by recipeId for quick lookup when pre-populating
  const existingByRecipeId = useMemo(() => {
    const map = {}
    ;(existingSoldRecipes || []).forEach(item => {
      if (!item || !item.recipeId) return
      const id =
        (typeof item.recipeId === 'string'
          ? item.recipeId
          : item.recipeId?.toString?.()) || null
      if (!id) return
      map[id] = item
    })
    return map
  }, [existingSoldRecipes])

  const filteredRecipes = useMemo(() => {
    const term = (searchTerm || '').trim().toLowerCase()
    if (!term) return recipes || []

    return (recipes || []).filter(recipe =>
      (recipe?.title || '').toLowerCase().includes(term),
    )
  }, [recipes, searchTerm])

  // Initialise local state for sold recipes when modal opens or existingSoldRecipes change
  React.useEffect(() => {
    if (!visible) return

    const initialMap = {}
    ;(recipes || []).forEach(recipe => {
      const id = recipe._id || recipe.id
      const idStr =
        (typeof id === 'string' ? id : id?.toString?.()) || undefined
      if (!idStr) return

      const existing = existingByRecipeId[idStr] || {}

      initialMap[idStr] = {
        recipeId: idStr,
        recipeName: recipe.title,
        unitType: existing.unitType || 'Portions',
        qtySold:
          typeof existing.qtySold === 'number'
            ? existing.qtySold
            : typeof existing.totalPortionsSold === 'number'
            ? existing.totalPortionsSold
            : 0,
      }
    })

    setSoldByRecipeId(initialMap)
  }, [visible, recipes, existingByRecipeId])

  const columns = [
    {
      title: 'Recipe Name',
      dataIndex: 'title',
      key: 'title',
      width: 260,
    },
    {
      title: (
        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          Unit Type
          <Tooltip title="Choose whether your quantity is in portions or full recipes. 'Full Recipe' multiplies by the number of portions this recipe makes.">
            <InfoCircleOutlined style={{ fontSize: 12, color: '#8c8c8c' }} />
          </Tooltip>
        </span>
      ),
      dataIndex: 'unitType',
      key: 'unitType',
      align: 'center',
      width: 160,
      render: (_value, record) => {
        const current = soldByRecipeId[record.recipeId] || {}
        return (
          <Select
            value={current.unitType || 'Portions'}
            style={{ width: 140 }}
            onChange={value => {
              setSoldByRecipeId(prev => ({
                ...prev,
                [record.recipeId]: {
                  ...(prev[record.recipeId] || {}),
                  recipeId: record.recipeId,
                  recipeName: record.title,
                  unitType: value,
                  qtySold: prev[record.recipeId]?.qtySold ?? 0,
                },
              }))
            }}
          >
            <Option value='Portions'>Portions</Option>
            <Option value='Full Recipe'>Full Recipe</Option>
          </Select>
        )
      },
    },
    {
      title: quantityLabel,
      dataIndex: 'qtySold',
      key: 'qtySold',
      align: 'right',
      width: 140,
      render: (_value, record) => {
        const current = soldByRecipeId[record.recipeId] || {}
        return (
          <InputNumber
            min={0}
            value={current.qtySold ?? 0}
            style={{ width: 120 }}
            onChange={value => {
              const safeValue =
                value === null || value === undefined
                  ? 0
                  : Number.isFinite(Number(value)) && Number(value) >= 0
                  ? Number(value)
                  : 0

              setSoldByRecipeId(prev => ({
                ...prev,
                [record.recipeId]: {
                  ...(prev[record.recipeId] || {}),
                  recipeId: record.recipeId,
                  recipeName: record.title,
                  unitType: prev[record.recipeId]?.unitType || 'Portions',
                  qtySold: safeValue,
                },
              }))
            }}
          />
        )
      },
    },
    {
      title: (
        <Tooltip title='Cost per portion or per full recipe, depending on Unit Type.'>
          <span>Unit Cost</span>
        </Tooltip>
      ),
      key: 'unitCost',
      align: 'right',
      width: 140,
      render: (_value, record) => {
        const recipe =
          (recipes || []).find(r => {
            const id = r._id || r.id
            const idStr =
              (typeof id === 'string' ? id : id?.toString?.()) || undefined
            return idStr === record.recipeId
          }) || {}

        const current = soldByRecipeId[record.recipeId] || {}
        const unitType = current.unitType || record.unitType || 'Portions'

        const costPerPortion = Number(recipe.costPerPortion) || 0
        const portions = Number(recipe.portions) || 1
        const costPerRecipe =
          Number(recipe.costPerRecipe) || costPerPortion * portions

        const unitCost =
          unitType === 'Full Recipe' ? costPerRecipe : costPerPortion

        return (
          <Tooltip title='Cost per portion or per full recipe, based on Unit Type.'>
            <span>£{unitCost.toFixed(2)}</span>
          </Tooltip>
        )
      },
    },
    {
      title: (
        <Tooltip title='Total Cost = Unit Cost × Qty Sold.'>
          <span>Total Cost</span>
        </Tooltip>
      ),
      key: 'totalCost',
      align: 'right',
      width: 160,
      render: (_value, record) => {
        const recipe =
          (recipes || []).find(r => {
            const id = r._id || r.id
            const idStr =
              (typeof id === 'string' ? id : id?.toString?.()) || undefined
            return idStr === record.recipeId
          }) || {}

        const current = soldByRecipeId[record.recipeId] || {}
        const unitType = current.unitType || record.unitType || 'Portions'
        const qtySold = Number(current.qtySold ?? record.qtySold ?? 0) || 0

        const costPerPortion = Number(recipe.costPerPortion) || 0
        const portions = Number(recipe.portions) || 1
        const costPerRecipe =
          Number(recipe.costPerRecipe) || costPerPortion * portions

        const unitCost =
          unitType === 'Full Recipe' ? costPerRecipe : costPerPortion
        const totalCost = unitCost * qtySold

        return (
          <Tooltip title='Total Cost = Unit Cost × Qty Sold.'>
            <span>£{totalCost.toFixed(2)}</span>
          </Tooltip>
        )
      },
    },
  ]

  // Data source – filtered by search term and pre-populated from existingSoldRecipes (if editing)
  const dataSource = (filteredRecipes || []).map(recipe => {
    const id = recipe._id || recipe.id
    const idStr = (typeof id === 'string' ? id : id?.toString?.()) || undefined
    const existing = (idStr && existingByRecipeId[idStr]) || {}

    const current = soldByRecipeId[idStr] || {}

    return {
      key: idStr || id,
      recipeId: idStr || id,
      title: recipe.title,
      unitType: current.unitType || existing.unitType || 'Portions',
      qtySold:
        typeof current.qtySold === 'number'
          ? current.qtySold
          : typeof existing.qtySold === 'number'
          ? existing.qtySold
          : typeof existing.totalPortionsSold === 'number'
          ? existing.totalPortionsSold
          : 0,
    }
  })

  const handleOk = () => {
    if (!onSave) return
    if (showReason && !reason) {
      setReasonError('Choose a reason')
      return
    }
    setReasonError('')
    const result = buildRecipeEntriesFromState(soldByRecipeId, recipes)
    onSave(result, reason)
  }

  return (
    <Modal
      title={modalTitle}
      visible={visible}
      onCancel={onCancel}
      onOk={handleOk}
      okText={okText}
      cancelText='Cancel'
      confirmLoading={confirmLoading}
      width={900}
      destroyOnClose
    >
      <Typography style={{ marginBottom: 16 }}>
        <Title level={5}>{modalTitle}</Title>
        <Paragraph type='secondary'>{helperDescription}</Paragraph>
        {recordedOn ? (
          <Paragraph data-testid='wasted-recipes-date'>{recordedOn}</Paragraph>
        ) : null}
      </Typography>
      {showReason ? (
        <ReasonRow data-testid='wasted-recipe-reasons'>
          {WASTAGE_REASON_CHIPS.map(chip => (
            <ReasonChip
              key={chip.id}
              type='button'
              data-testid={`wasted-recipe-reason-${chip.id}`}
              aria-pressed={reason === chip.id}
              onClick={() => {
                if (onReason) onReason(chip.id)
                setReasonError('')
              }}
            >
              {chip.label}
            </ReasonChip>
          ))}
          {reasonError ? (
            <ReasonError data-testid='wasted-recipe-reason-error'>
              {reasonError}
            </ReasonError>
          ) : null}
        </ReasonRow>
      ) : null}

      <Search
        placeholder='Search recipes…'
        allowClear
        value={searchTerm}
        onChange={e => setSearchTerm(e.target.value)}
        style={{ marginBottom: 16, maxWidth: 400, display: 'block' }}
      />

      <Table
        columns={columns}
        dataSource={dataSource}
        pagination={false}
        locale={{
          emptyText: searchTerm
            ? 'No recipes match your search.'
            : 'No recipes available. Create recipes in Menu Builder first.',
        }}
        scroll={{ y: 400, x: 'max-content' }}
      />

      {/* Preview: Ingredient Usage */}
      <IngredientUsagePreview
        soldByRecipeId={soldByRecipeId}
        recipes={recipes}
        products={products}
        isPartialStockTake={isPartialStockTake}
        partialScopeProductKeys={partialScopeProductKeys}
        quantityLabel={quantityLabel}
        usageField={usageField}
        usagePreviewConfig={usagePreviewConfig}
        previewTitle={resolvedPreviewTitle}
        previewEmptyText={resolvedPreviewEmptyText}
        previewErrorText={resolvedPreviewErrorText}
        usageQtyColumnLabel={resolvedUsageQtyColumnLabel}
        previewNote={previewNote}
      />
    </Modal>
  )
}

// Styled components for preview section
const PreviewContainer = styled.div`
  margin-top: 16px;
  border-top: 1px solid #f0f0f0;
  padding-top: 16px;
`

const PreviewHeader = styled.span`
  font-weight: 600;
  color: #262626;
`

const ReasonRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 16px;
`

const ReasonChip = styled.button`
  border: 1px solid #d9d9d9;
  background: #fff;
  border-radius: 999px;
  padding: 4px 10px;
  cursor: pointer;
  &[aria-pressed='true'] {
    border-color: #b5d922;
    background: rgba(181, 217, 34, 0.25);
  }
`

const ReasonError = styled.div`
  flex-basis: 100%;
  color: #b42318;
  font-size: 13px;
`

const PreviewContent = styled.div`
  margin-bottom: 16px;
  padding: 12px;
  background: #fafafa;
  border-radius: 4px;
`

const RecipesList = styled.div`
  margin-bottom: 12px;
  font-size: 14px;
  line-height: 1.6;

  ul {
    margin: 4px 0 0 0;
    padding-left: 20px;
  }

  li {
    margin-bottom: 4px;
  }
`

const StyledPreviewTable = styled(Table)`
  .ant-table-thead > tr > th {
    background: #f7fafc;
    font-weight: 600;
    color: #0f172a;
    border-bottom: 2px solid #e2e8f0;
  }

  .preview-row-even {
    background: #ffffff;
  }

  .preview-row-odd {
    background: #fafafa;
  }

  .ant-table-tbody > tr:hover {
    background: #f0f7ff !important;
  }

  .ant-table-tbody > tr > td {
    border-bottom: 1px solid #f0f0f0;
  }
`

// Preview component for ingredient usage
const IngredientUsagePreview = ({
  soldByRecipeId,
  recipes,
  products,
  isPartialStockTake = false,
  partialScopeProductKeys = [],
  quantityLabel = 'Qty Sold',
  usageField = 'sold',
  usagePreviewConfig = getUsagePreviewConfig('sold'),
  previewTitle = usagePreviewConfig.previewTitle,
  previewEmptyText = usagePreviewConfig.previewEmptyText,
  previewErrorText = usagePreviewConfig.previewErrorText,
  usageQtyColumnLabel = usagePreviewConfig.usageQtyColumnLabel,
  previewNote = '',
}) => {
  const productUsageField = usagePreviewConfig.productField || usageField
  const updateDescription =
    usagePreviewConfig.updateDescription ||
    (usageField === 'wastage' ? 'wastage quantities' : 'sold quantities')
  const recipesIncludedLabel =
    usagePreviewConfig.recipesIncludedLabel || 'Recipes included:'
  // Helper function to normalize supplier names for comparison
  const normalizeComparable = value =>
    String(value || '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, ' ')
      .trim()
  const normalizeSupplier = supplier => normalizeComparable(supplier)
  const normalizeSku = sku => normalizeComparable(sku).replace(/\s+/g, '')
  const normalizeTitle = title => normalizeComparable(title)
  const normalizeSize = size => normalizeComparable(size)
  const normalizeUom = uom => normalizeComparable(uom)
  const normalizeStoredScopeKey = rawKey => {
    const key = String(rawKey || '')
      .trim()
      .toLowerCase()
    if (!key) return ''
    const parts = key.split('::')
    if (parts.length >= 5) {
      const [title, supplierTitle, sku, size, uom] = parts
      return [
        normalizeTitle(title),
        normalizeSupplier(supplierTitle),
        normalizeSku(sku),
        normalizeSize(size),
        normalizeUom(uom),
      ].join('::')
    }
    return normalizeComparable(key)
  }
  const buildScopeKey = value =>
    [
      normalizeTitle(value?.title),
      normalizeSupplier(value?.supplierTitle),
      normalizeSku(value?.sku),
      normalizeSize(value?.size),
      normalizeUom(value?.UoM),
    ].join('::')
  const normalizedScopeKeys = new Set(
    (partialScopeProductKeys || [])
      .map(normalizeStoredScopeKey)
      .filter(Boolean),
  )
  const hasScopeRestriction = isPartialStockTake && normalizedScopeKeys.size > 0
  const isSupplierCompatible = (left, right) => {
    if (!left || !right) return true
    return left === right || left.includes(right) || right.includes(left)
  }

  // Helper function to format numbers with 2-3 decimals and remove trailing zeros
  const formatDecimal = (value, maxDecimals = 3) => {
    if (
      value === null ||
      value === undefined ||
      isNaN(value) ||
      !isFinite(value)
    ) {
      return 0
    }
    const num = Number(value)
    // Round to maxDecimals to handle floating-point errors
    const rounded =
      Math.round(num * Math.pow(10, maxDecimals)) / Math.pow(10, maxDecimals)
    // Convert to string and remove trailing zeros
    const str = rounded.toString()
    // If it's a whole number, return as-is
    if (str.indexOf('.') === -1) {
      return rounded
    }
    // Remove trailing zeros and trailing decimal point
    return parseFloat(str)
  }

  // Helper function to find matching product in stock take
  const findMatchingProduct = (usageEntry, products) => {
    // Strategy 1: Try to match by product ID if available
    const prodProductId =
      usageEntry.ingredientId?.toString() || usageEntry.ingredientId

    if (prodProductId) {
      const matchedById = products.find(prod => {
        if (
          hasScopeRestriction &&
          !normalizedScopeKeys.has(buildScopeKey(prod))
        ) {
          return false
        }
        const prodId =
          prod.venueProductId || prod.productId || prod.product?._id
        return prodId && prodId.toString() === prodProductId.toString()
      })
      if (matchedById) return matchedById
    }

    const matchedLinkedByMemberId = products.find(prod => {
      if (!prod?.isLinkedStockGroup) return false
      if (!Array.isArray(prod?.linkedVenueProductIds)) return false
      return prod.linkedVenueProductIds.some(
        linkedId => String(linkedId) === String(prodProductId || ''),
      )
    })
    if (matchedLinkedByMemberId) return matchedLinkedByMemberId

    // Strategy 2: Match by SKU + Supplier
    if (usageEntry.sku) {
      const entrySku = normalizeSku(usageEntry.sku)
      const entrySupplier = normalizeSupplier(usageEntry.supplierTitle)

      const bySkuSupplier = products.find(prod => {
        if (
          hasScopeRestriction &&
          !normalizedScopeKeys.has(buildScopeKey(prod))
        ) {
          return false
        }
        if (!prod.sku) return false
        const prodSku = normalizeSku(prod.sku)
        const prodSupplier = normalizeSupplier(prod.supplierTitle)
        if (!entrySku || !prodSku || prodSku !== entrySku) return false
        return isSupplierCompatible(prodSupplier, entrySupplier)
      })
      if (bySkuSupplier) return bySkuSupplier
    }

    const entryTitle = normalizeTitle(usageEntry.title)
    if (entryTitle) {
      const entrySupplier = normalizeSupplier(usageEntry.supplierTitle)
      const byTitle = products.find(prod => {
        if (
          hasScopeRestriction &&
          !normalizedScopeKeys.has(buildScopeKey(prod))
        ) {
          return false
        }
        const prodTitle = normalizeTitle(prod.title)
        if (!prodTitle || prodTitle !== entryTitle) return false
        const prodSupplier = normalizeSupplier(prod.supplierTitle)
        return isSupplierCompatible(prodSupplier, entrySupplier)
      })
      if (byTitle) return byTitle
    }

    return null
  }

  // Calculate ingredient usage based on current soldByRecipeId state
  const previewData = useMemo(() => {
    try {
      // Build recipe entries array from current state
      const soldRecipesArray = buildRecipeEntriesFromState(
        soldByRecipeId,
        recipes,
      )

      if (soldRecipesArray.length === 0) {
        return { recipes: [], hasData: false }
      }

      // Calculate ingredient usage
      const ingredientUsageMap = calculateIngredientUsage(
        soldRecipesArray,
        recipes,
      )

      if (
        !ingredientUsageMap ||
        Object.keys(ingredientUsageMap || {}).length === 0
      ) {
        return { recipes: [], hasData: false }
      }

      const usageEntries = Object.values(ingredientUsageMap || {})

      // For each usage entry, create ingredient preview entries
      // Note: calculateIngredientUsage aggregates ingredients across all recipes,
      // so we show the aggregated totals here
      const ingredientPreviews = usageEntries.map(entry => {
        const matchingProduct = findMatchingProduct(entry, products)
        const currentUsage = matchingProduct
          ? Number(matchingProduct[productUsageField]) || 0
          : 0
        const usageQty = Number(entry.quantity) || 0
        const newUsage = currentUsage + usageQty
        const willApply = isPartialStockTake ? Boolean(matchingProduct) : true

        return {
          ingredientId: entry.ingredientId,
          title: entry.title || '',
          sku: entry.sku || '',
          supplierTitle: entry.supplierTitle || '',
          UoM: entry.UoM || '',
          usageQty,
          [usagePreviewConfig.currentKey]: currentUsage,
          [usagePreviewConfig.newKey]: newUsage,
          isNew: !matchingProduct,
          willApply,
          isSkippedOutsideGroup: isPartialStockTake && !matchingProduct,
        }
      })

      const applyCount = ingredientPreviews.filter(ing => ing.willApply).length
      const skippedCount = ingredientPreviews.filter(
        ing => ing.isSkippedOutsideGroup,
      ).length

      return {
        recipes: soldRecipesArray,
        ingredients: ingredientPreviews,
        hasData: true,
        totalIngredients: ingredientPreviews.length,
        newIngredients: ingredientPreviews.filter(ing => ing.isNew).length,
        applyCount,
        skippedCount,
      }
    } catch (error) {
      console.error('Error calculating ingredient usage preview:', error)
      return { recipes: [], hasData: false, error: true }
    }
  }, [
    soldByRecipeId,
    recipes,
    products,
    isPartialStockTake,
    partialScopeProductKeys,
    productUsageField,
    usagePreviewConfig,
  ])

  // Don't show preview if no data
  if (!previewData.hasData) {
    return (
      <PreviewContainer>
        <Collapse defaultActiveKey={[]}>
          <Panel
            header={<PreviewHeader>{previewTitle}</PreviewHeader>}
            key='preview'
          >
            <Paragraph type='secondary' style={{ margin: 0 }}>
              {previewEmptyText}
            </Paragraph>
          </Panel>
        </Collapse>
      </PreviewContainer>
    )
  }

  if (previewData.error) {
    return (
      <PreviewContainer>
        <Collapse defaultActiveKey={[]}>
          <Panel
            header={<PreviewHeader>{previewTitle}</PreviewHeader>}
            key='preview'
          >
            <Paragraph type='secondary' style={{ margin: 0, color: '#ff4d4f' }}>
              {previewErrorText}
            </Paragraph>
          </Panel>
        </Collapse>
      </PreviewContainer>
    )
  }

  return (
    <PreviewContainer>
      <Collapse defaultActiveKey={[]}>
        <Panel
          header={
            <PreviewHeader>
              <span>
                {previewTitle}{' '}
                <span
                  style={{
                    fontSize: 12,
                    fontWeight: 'normal',
                    color: '#8c8c8c',
                  }}
                >
                  ({previewData.totalIngredients} ingredient
                  {previewData.totalIngredients !== 1 ? 's' : ''}
                  {previewData.newIngredients > 0 &&
                    `, ${previewData.newIngredients} new`}
                  )
                </span>
              </span>
            </PreviewHeader>
          }
          key='preview'
        >
          <PreviewContent>
            <Paragraph type='secondary' style={{ marginBottom: 12 }}>
              {previewNote
                ? previewNote
                : isPartialStockTake
                ? 'Partial count scope: only ingredients already in this partial group will be applied.'
                : `The following ingredients will be updated in your stock take (${updateDescription}).`}{' '}
              Quantities are aggregated across all selected recipes.
            </Paragraph>
            {isPartialStockTake ? (
              <Paragraph type='secondary' style={{ marginBottom: 12 }}>
                {`${previewData.applyCount || 0} ingredient(s) will apply, ${
                  previewData.skippedCount || 0
                } will be skipped (outside this partial group).`}
              </Paragraph>
            ) : null}
            {previewData.recipes.length > 0 && (
              <RecipesList>
                <strong>{recipesIncludedLabel}</strong>
                <ul style={{ margin: '4px 0 0 0', paddingLeft: 20 }}>
                  {previewData.recipes.map((r, idx) => (
                    <li key={idx}>
                      {r.recipeName} ({r.qtySold} {r.unitType}, {quantityLabel})
                    </li>
                  ))}
                </ul>
              </RecipesList>
            )}
          </PreviewContent>
          {previewData.ingredients && previewData.ingredients.length > 0 ? (
            <StyledPreviewTable
              dataSource={previewData.ingredients}
              rowKey={(record, index) =>
                record.ingredientId || record.sku || `ingredient-${index}`
              }
              rowClassName={(record, index) =>
                index % 2 === 0 ? 'preview-row-even' : 'preview-row-odd'
              }
              columns={[
                {
                  title: 'Ingredient',
                  dataIndex: 'title',
                  key: 'title',
                  width: 180,
                },
                {
                  title: 'SKU',
                  dataIndex: 'sku',
                  key: 'sku',
                  width: 100,
                },
                {
                  title: 'Supplier',
                  dataIndex: 'supplierTitle',
                  key: 'supplier',
                  width: 140,
                },
                {
                  title: 'Status',
                  key: 'status',
                  width: 180,
                  render: (_, record) => {
                    if (record.isSkippedOutsideGroup) {
                      return (
                        <Tag color='orange' style={{ margin: 0 }}>
                          Skipped (outside group)
                        </Tag>
                      )
                    }
                    if (record.willApply) {
                      return (
                        <Tag color='green' style={{ margin: 0 }}>
                          Will apply
                        </Tag>
                      )
                    }
                    if (record.isNew) {
                      return (
                        <Tag color='green' style={{ margin: 0 }}>
                          New
                        </Tag>
                      )
                    }
                    return (
                      <Tag color='blue' style={{ margin: 0 }}>
                        Existing
                      </Tag>
                    )
                  },
                },
                {
                  title: usageQtyColumnLabel,
                  dataIndex: 'usageQty',
                  key: 'usageQty',
                  align: 'right',
                  width: 110,
                  render: (value, record) => (
                    <span>
                      {formatDecimal(value || 0)} {record.UoM || ''}
                    </span>
                  ),
                },
                {
                  title: usagePreviewConfig.currentLabel,
                  dataIndex: usagePreviewConfig.currentKey,
                  key: usagePreviewConfig.currentKey,
                  align: 'right',
                  width: 120,
                  render: value => <span>{formatDecimal(value || 0)}</span>,
                },
                {
                  title: usagePreviewConfig.newLabel,
                  dataIndex: usagePreviewConfig.newKey,
                  key: usagePreviewConfig.newKey,
                  align: 'right',
                  width: 120,
                  render: (value, record) => (
                    <span
                      style={{
                        fontWeight: 'bold',
                        color: record.isNew ? '#52c41a' : '#1890ff',
                      }}
                    >
                      {formatDecimal(value || 0)}
                    </span>
                  ),
                },
              ]}
              pagination={false}
              size='small'
              bordered
              scroll={{ x: 'max-content' }}
            />
          ) : (
            <Paragraph type='secondary' style={{ margin: 0 }}>
              No ingredients found for the selected recipes.
            </Paragraph>
          )}
        </Panel>
      </Collapse>
    </PreviewContainer>
  )
}

export default AddSoldRecipesModal
